// src/App.js
// Thin shell only: session, top navbar, and which page is showing. No page
// logic lives here — each screen is its own file under pages/. Website shell
// (full-width top navbar + footer, no sidebar) — openart.ai-style.

import { useEffect, useRef, useState } from "react";
import "./App.css";

import Navbar from "./components/Navbar";
import AuthModal from "./components/AuthModal";
import Footer from "./components/Footer";
import StorageExpiryBanner from "./components/StorageExpiryBanner";

import Landing from "./pages/Landing";
import Home from "./pages/Home";
import DriftFeed from "./pages/DriftFeed";
import Characters from "./pages/Characters";
import CreateImage from "./pages/CreateImage";
import CreateVideo from "./pages/CreateVideo";
import MyDrops from "./pages/MyDrops";
import Credits from "./pages/Credits";
import Subscribe from "./pages/Subscribe";
import CreateCharacter from "./pages/CreateCharacter";
import Notifications from "./pages/Notifications";
import Profile from "./pages/Profile";
import CreatorProfile from "./pages/CreatorProfile";
import Assistant from "./pages/Assistant";
import Legal from "./pages/Legal";
import Studio from "./pages/Studio";

import { auth, notifications as notifApi, getToken, clearToken } from "./api";

export default function App() {
  const [user, setUser] = useState(null);
  const [booting, setBooting] = useState(true);
  const [showAuth, setShowAuth] = useState(false);
  const [authTab, setAuthTab] = useState("login"); // "login" | "signup"
  const [route, setRoute] = useState("landing"); // landing | drift | characters | create | image | video | drops | credits | subscribe ...
  const [remix, setRemix] = useState(null); // a Drift post being remixed into a studio
  const [pendingBot, setPendingBot] = useState(null); // open this bot's chat on the Characters page
  const [creatorId, setCreatorId] = useState(null); // creator profile being viewed
  const [legalDoc, setLegalDoc] = useState("index"); // which legal page
  const [unread, setUnread] = useState(0);
  // A Studio project link that bounced an unauthenticated user out to Karvi
  // login (via ?studio_return=<path>). Kept in state so it can be handed to
  // <Studio> once we route there; kept in a ref too so the async auth check
  // below (whose closure is fixed at mount) can read the freshly-parsed
  // value without waiting for a re-render.
  const [pendingStudioReturn, setPendingStudioReturn] = useState(null);
  const studioReturnRef = useRef(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sr = params.get("studio_return");
    if (!sr) return;
    studioReturnRef.current = sr;
    setPendingStudioReturn(sr);
    params.delete("studio_return");
    const rest = params.toString();
    window.history.replaceState({}, "", window.location.pathname + (rest ? `?${rest}` : ""));
  }, []);

  useEffect(() => {
    if (!getToken()) {
      setBooting(false);
      // Already-parsed above (this effect runs after it, in declaration order).
      if (studioReturnRef.current) {
        setAuthTab("login");
        setShowAuth(true);
      }
      return;
    }
    auth.me()
      .then((u) => {
        setUser(u);
        const goStudio = studioReturnRef.current;
        studioReturnRef.current = null;
        setRoute(goStudio ? "studio" : "home");
      })
      .catch(() => clearToken())
      .finally(() => setBooting(false));
  }, []);

  // Poll the unread notification count while logged in.
  useEffect(() => {
    if (!user) return setUnread(0);
    let alive = true;
    const tick = () => notifApi.unreadCount().then((d) => alive && setUnread(d.unread_count || 0)).catch(() => {});
    tick();
    const t = setInterval(tick, 60000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [user]);

  const navigate = (name) => {
    // studios, drops, billing, create, profile and notifications require an account
    if (["home", "image", "video", "studio", "drops", "credits", "subscribe", "create", "profile", "notifications", "assistant"].includes(name) && !user) {
      setAuthTab("login");
      return setShowAuth(true);
    }
    if (name !== "image" && name !== "video") setRemix(null);
    if (name !== "characters") setPendingBot(null);
    window.scrollTo(0, 0);
    setRoute(name);
  };

  const openChatWith = (bot) => {
    setPendingBot(bot);
    setRoute("characters");
  };

  const openCreator = (id) => {
    if (!id) return;
    setCreatorId(id);
    setRoute("creator");
  };

  const openLegal = (which = "index") => {
    setLegalDoc(which);
    setRoute("legal");
  };

  const logout = () => {
    clearToken();
    setUser(null);
    setRoute("landing");
  };

  const openSignup = () => {
    setAuthTab("signup");
    setShowAuth(true);
  };

  // A Drift post → open the matching studio prefilled from its data.
  const startRemix = (post) => {
    const genType =
      post.generation_type ||
      post.recreate_payload?.generation_type ||
      post.recreate_payload?.generation_mode;
    const isImage = ["text_to_image", "refx", "pixsync"].includes(genType) || post.type === "image";
    setRemix(post);
    setRoute(isImage ? "image" : "video");
  };

  const setCredits = (credits) => setUser((u) => (u ? { ...u, credits } : u));

  if (booting) {
    return (
      <div className="boot">
        <span className="grad-text boot__mark">Karvi AI</span>
      </div>
    );
  }

  return (
    <div className="app">
      <Navbar
        active={route === "subscribe" ? "credits" : route}
        onNavigate={navigate}
        user={user}
        unread={unread}
        onLogin={() => { setAuthTab("login"); setShowAuth(true); }}
        onSignup={openSignup}
        onLogout={logout}
        onNotifications={() => navigate("notifications")}
        onProfile={() => navigate("profile")}
      />

      <StorageExpiryBanner user={user} />

      <main className="main">
        {route === "landing" && <Landing onNavigate={navigate} onSignup={openSignup} />}
        {route === "home" && <Home onRecreate={startRemix} onNavigate={navigate} />}
        {route === "drift" && (
          <DriftFeed user={user} onRequireAuth={() => setShowAuth(true)} onRemix={startRemix} onOpenCreator={openCreator} />
        )}
        {route === "creator" && (
          <CreatorProfile
            userId={creatorId}
            user={user}
            onRequireAuth={() => setShowAuth(true)}
            onRemix={startRemix}
            onBack={() => setRoute("drift")}
          />
        )}
        {route === "characters" && (
          <Characters
            user={user}
            initialBot={pendingBot}
            onRequireAuth={() => setShowAuth(true)}
            onQuota={(q) => setUser((u) => (u ? { ...u, ...q } : u))}
          />
        )}
        {route === "create" && <CreateCharacter onOpenChat={openChatWith} />}
        {route === "assistant" && <Assistant />}
        {route === "image" && (
          <CreateImage user={user} onCreditsChange={setCredits} remix={remix} onNeedCredits={() => navigate("credits")} />
        )}
        {route === "video" && (
          <CreateVideo user={user} onCreditsChange={setCredits} remix={remix} onNeedCredits={() => navigate("credits")} />
        )}
        {route === "studio" && <Studio returnPath={pendingStudioReturn} />}
        {route === "drops" && <MyDrops />}
        {route === "credits" && <Credits user={user} onUser={setUser} onNavigate={navigate} />}
        {route === "subscribe" && <Subscribe user={user} onUser={setUser} onNavigate={navigate} />}
        {route === "notifications" && <Notifications onUnread={setUnread} />}
        {route === "profile" && <Profile user={user} onUser={setUser} onLoggedOut={logout} onOpenLegal={openLegal} />}
        {route === "legal" && <Legal doc={legalDoc} onOpen={openLegal} onBack={() => setLegalDoc("index")} />}
      </main>

      {route !== "studio" && <Footer onNavigate={navigate} onOpenLegal={openLegal} />}

      {showAuth && (
        <AuthModal
          initialTab={authTab}
          onClose={() => setShowAuth(false)}
          onAuthed={(u) => {
            setUser(u);
            setShowAuth(false);
            const goStudio = studioReturnRef.current;
            studioReturnRef.current = null;
            setRoute(goStudio ? "studio" : "drift");
          }}
          onOpenLegal={(k) => {
            setShowAuth(false);
            openLegal(k);
          }}
        />
      )}
    </div>
  );
}
